import React, { useEffect, useMemo, useState } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { Activity, ChevronDown, ChevronRight, FileText, PanelBottom, PanelTop, Search } from 'lucide-react';

const groupLabel = (dir) => (dir === '' ? 'Pages' : dir);
const pageLabel = (page) => (page.path === 'site/index.html' ? 'Home' : page.slug);

const SHARED = [
  { scope: 'header', label: 'Header', Icon: PanelTop },
  { scope: 'footer', label: 'Footer', Icon: PanelBottom },
];

const PageSidebar = ({ pages, loading, error, selectedPath, shared, health, dirty, onSelect, onSelectShared, onSelectHealth }) => {
  const [filter, setFilter] = useState('');
  const [open, setOpen] = useState(() => new Set(['']));

  const selectedDir = useMemo(() => pages.find((p) => p.path === selectedPath)?.dir, [pages, selectedPath]);

  // Keep the selected page's group open, e.g. after following a link to another section.
  useEffect(() => {
    if (selectedDir !== undefined) {
      setOpen((prev) => (prev.has(selectedDir) ? prev : new Set(prev).add(selectedDir)));
    }
  }, [selectedDir]);

  const groups = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const byDir = new Map();
    for (const page of pages) {
      if (q && !page.url.toLowerCase().includes(q) && !pageLabel(page).toLowerCase().includes(q)) continue;
      if (!byDir.has(page.dir)) byDir.set(page.dir, []);
      byDir.get(page.dir).push(page);
    }
    // Home first; the server sorts by file name, where it is "index".
    const root = byDir.get('');
    if (root) root.sort((a, b) => (b.path === 'site/index.html') - (a.path === 'site/index.html'));
    return [...byDir.entries()];
  }, [pages, filter]);

  const filtering = filter.trim().length > 0;

  const toggle = (dir) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(dir)) next.delete(dir);
      else next.add(dir);
      return next;
    });

  return (
    <aside className="mse-sidebar">
      <div className="mse-sidebar-search">
        <Search size={14} className="mse-sidebar-search-icon" />
        <Form.Control
          size="sm"
          type="search"
          placeholder={`Filter ${pages.length || ''} pages`}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          aria-label="Filter pages"
        />
      </div>
      {loading && (
        <div className="mse-empty">
          <Spinner animation="border" size="sm" /> Loading pages
        </div>
      )}
      {error && <div className="mse-empty text-danger">{error}</div>}
      {!loading && !error && groups.length === 0 && <div className="mse-empty">No pages match.</div>}
      <nav className="mse-page-groups" aria-label="Marketing site pages">
        <div className="mse-page-group mse-shared-group">
          <div className="mse-group-toggle mse-group-static">
            <span className="mse-group-name">Site</span>
          </div>
          <button
            type="button"
            className={`mse-page-link${health ? ' active' : ''}`}
            aria-current={health ? 'page' : undefined}
            onClick={onSelectHealth}
            title="SEO and other checks across every page"
          >
            <Activity size={14} />
            <span className="mse-page-name">Site health</span>
          </button>
          <div className="mse-group-toggle mse-group-static">
            <span className="mse-group-name">Shared</span>
            <span className="mse-group-count">all pages</span>
          </div>
          {SHARED.map(({ scope, label, Icon }) => (
            <button
              key={scope}
              type="button"
              className={`mse-page-link${shared === scope ? ' active' : ''}`}
              aria-current={shared === scope ? 'page' : undefined}
              onClick={() => onSelectShared(scope)}
              title={`Edit the ${scope} shared by every page`}
            >
              <Icon size={14} />
              <span className="mse-page-name">{label}</span>
              {dirty && shared === scope && <span className="mse-dirty-dot" title="Unsaved changes" />}
            </button>
          ))}
        </div>
        {groups.map(([dir, items]) => {
          const expanded = filtering || open.has(dir);
          return (
            <div key={dir || '(root)'} className="mse-page-group">
              <button type="button" className="mse-group-toggle" onClick={() => toggle(dir)} aria-expanded={expanded}>
                {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                <span className="mse-group-name">{groupLabel(dir)}</span>
                <span className="mse-group-count">{items.length}</span>
              </button>
              {expanded &&
                items.map((page) => {
                  const active = !shared && !health && page.path === selectedPath;
                  return (
                    <button
                      key={page.path}
                      type="button"
                      className={`mse-page-link${active ? ' active' : ''}`}
                      aria-current={active ? 'page' : undefined}
                      onClick={() => onSelect(page.path)}
                      title={page.url}
                    >
                      <FileText size={14} />
                      <span className="mse-page-name">{pageLabel(page)}</span>
                      {dirty && active && <span className="mse-dirty-dot" title="Unsaved changes" />}
                    </button>
                  );
                })}
            </div>
          );
        })}
      </nav>
    </aside>
  );
};

export default PageSidebar;
